import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../services/firestore';
import { adminService } from '../../services/adminService';
import { errorMessage } from '../../services/finesService';
import Card from '../../ui/Card';
import DataTable from '../../ui/DataTable/DataTable';
import { CellMuted, CellNumber, CellStatus, CellText } from '../../ui/DataTable/cells';
import Button from '../../ui/Button';
import { Field, Input, Select } from '../../ui/Field/Field';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import logger from '../../utils/logger';
import { dubaiDateTime, dubaiDayStartMs, DAY_MS } from '../../utils/dubaiTime';

const PAGE = 100;

export const KINDS = [
    { id: '', label: 'Everything' },
    { id: 'changes', label: 'Changes (saved, deleted, run)' },
    { id: 'failed', label: 'Refused / failed changes' },
    { id: 'pages', label: 'Pages opened' },
    { id: 'sign-in', label: 'Sign in / out' },
];

/** The colour of a line: refused / failed red, deletes red, changes amber, pages and sign-ins calm. */
export const toneOf = (row) => {
    const status = Number(row?.metadata?.status);
    const action = String(row?.action_type || '');
    if (status >= 400) return 'danger';
    if (action.startsWith('DELETE')) return 'danger';
    if (action === 'LOGIN') return 'success';
    if (action === 'OPEN_PAGE' || action === 'LOGOUT') return 'neutral';
    if (action.startsWith('UPDATE') || action.startsWith('RUN')) return 'warning';
    return 'info';
};

/** Where a line came from: the engine logged a change itself, or the page wrote it. */
const sourceOf = (row) => (row?.metadata?.source === 'engine' ? 'Engine' : 'Page');

/**
 * Administration > Activity log (owner, 20 Oct 2026: "عايز اطور اللوج ده علشان يسجل كل حاجه بيعملها اى يوزر واخليه
 * ضمن صفحه الادمن مع صلاحيات الادمن بس"): every change a user made (logged by the engine itself), every page
 * opened, sign-ins and the pages' own lines - newest first, kept 6 months. The Admin role only (the engine refuses
 * anyone else); it was History > User Activity.
 */
export default function ActivityLogPanel() {
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState({ userEmail: '', kind: '', q: '', from: '', to: '' });
    const [typed, setTyped] = useState('');
    const [rows, setRows] = useState([]);
    const [next, setNext] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        getDocs(query(collection(db, 'users'), orderBy('name')))
            .then((snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
            .catch((err) => logger.error('Activity log: users not read', err));
    }, []);

    // the search box applies itself after a short pause (not one request per key)
    useEffect(() => {
        const t = setTimeout(() => setFilters((f) => (f.q === typed ? f : { ...f, q: typed })), 400);
        return () => clearTimeout(t);
    }, [typed]);

    // only the newest request's answer counts (a slow search, or "Load more" of the filters before, never wins)
    const latest = useRef(0);

    const load = useCallback(async (before = null) => {
        const ticket = ++latest.current;
        setLoading(true);
        setError('');
        try {
            const answer = await adminService.getLogPage({
                userEmail: filters.userEmail,
                kind: filters.kind,
                q: filters.q,
                startDate: filters.from ? new Date(dubaiDayStartMs(filters.from)) : null,
                endDate: filters.to ? new Date(dubaiDayStartMs(filters.to) + DAY_MS - 1) : null,
            }, PAGE, before);
            if (ticket !== latest.current) return;
            setRows((prev) => (before ? [...prev, ...answer.items] : answer.items));
            setNext(answer.next || null);
        } catch (err) {
            if (ticket !== latest.current) return;
            logger.error('Activity log: not read', err);
            setError(err?.code === 'permission-denied'
                ? 'Only the Admin role can read the activity log.'
                : `The activity log could not be read: ${errorMessage(err)}`);
        } finally {
            if (ticket === latest.current) setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(null); }, [load]);

    const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

    const columns = [
        { key: '__n', header: '#', width: 56, sortable: false, searchable: false, exportable: false, hideable: false,
            cell: (row, value, i) => <CellNumber value={i + 1} /> },
        {
            key: 'timestamp',
            header: 'Time (Dubai)',
            accessor: (row) => (row.timestamp ? dubaiDateTime(row.timestamp, { seconds: true }) : ''),
            sortValue: (row) => (row.timestamp ? new Date(row.timestamp).getTime() : null),
            cell: (row) => (row.timestamp
                ? <span className="ds-cell-date whitespace-nowrap">{dubaiDateTime(row.timestamp, { seconds: true })}</span>
                : <span className="ds-cell-empty">—</span>),
        },
        {
            key: 'user_email',
            header: 'User',
            cell: (row) => (
                <div className="flex flex-col items-center">
                    <CellText>{row.user_email || '—'}</CellText>
                    <CellMuted>{row.user_role || ''}</CellMuted>
                </div>
            ),
        },
        {
            key: 'action_type',
            header: 'Action',
            cell: (row) => <CellStatus status={row.action_type} label={row.action_type} tone={toneOf(row)} />,
        },
        {
            key: 'details',
            header: 'Details',
            nowrap: false,
            cell: (row) => <CellText max={320} title={row.details}>{row.details}</CellText>,
        },
        {
            key: 'result',
            header: 'Result',
            accessor: (row) => (row.metadata?.status ? String(row.metadata.status) : ''),
            cell: (row) => (row.metadata?.status
                ? <CellStatus status={String(row.metadata.status)} label={`${row.metadata.status}`}
                    tone={Number(row.metadata.status) >= 400 ? 'danger' : 'success'} />
                : <span className="ds-cell-empty">—</span>),
        },
        { key: 'source', header: 'Source', accessor: sourceOf, cell: (row) => <CellMuted>{sourceOf(row)}</CellMuted> },
        { key: 'metadata', header: 'Data', hidden: true, accessor: (row) => JSON.stringify(row.metadata || {}) },
    ];

    return (
        <Card
            title="Activity log"
            subtitle="Everything every user did: changes (logged by the engine), pages opened, sign-ins. Kept 6 months. Click a row for its data."
        >
            <DataTable
                columns={columns}
                data={rows}
                rowKey="id"
                loading={loading && rows.length === 0}
                error={error || null}
                searchable={false}
                pagination={false}
                card={false}
                maxHeight="70vh"
                exportFileName="activity-log"
                persistKey="admin-activity-log"
                emptyTitle="No activity for these filters."
                renderExpanded={(row) => (
                    <pre className="whitespace-pre-wrap break-all text-left font-mono text-xs text-gray-700">
                        {JSON.stringify(row.metadata || {}, null, 2)}
                    </pre>
                )}
                filters={(
                    <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-3 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto_auto_auto]">
                        <Field label="User">
                            <Select value={filters.userEmail} onChange={set('userEmail')}>
                                <option value="">All users</option>
                                {users.map((u) => (
                                    <option key={u.uid || u.id} value={u.email}>{u.name} ({u.email})</option>
                                ))}
                            </Select>
                        </Field>
                        <Field label="Kind">
                            <Select value={filters.kind} onChange={set('kind')}>
                                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                            </Select>
                        </Field>
                        <Field label="Search">
                            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Contract, plate, page, action…" />
                        </Field>
                        <Field label="From">
                            <Input type="date" value={filters.from} onChange={set('from')} />
                        </Field>
                        <Field label="To">
                            <Input type="date" value={filters.to} onChange={set('to')} />
                        </Field>
                        <Button variant="primary" onClick={() => load(null)} isLoading={loading}
                            leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>
                            Refresh
                        </Button>
                    </div>
                )}
                footer={(
                    <div className="flex items-center justify-center gap-3 text-sm text-gray-700">
                        <span>{rows.length.toLocaleString()} line(s) shown</span>
                        {next && (
                            <Button size="sm" onClick={() => load(next)} isLoading={loading} disabled={loading}>Load {PAGE} more</Button>
                        )}
                    </div>
                )}
            />
        </Card>
    );
}
